import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ViewTypeName } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useShortIdContext } from '@air/provider-short-id';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { PUBLIC_TOP_BAR_HEIGHT } from '~/components/PublicTopBar';
import { PublicSearchResultsNumber } from '~/components/Search/PublicSearchResultsNumber';
import { ViewControls, type ViewControlsProps } from '~/components/ViewControls/ViewControls';
import { ViewKanbanGroupByMenu } from '~/components/ViewControls/ViewKanbanGroupByMenu/ViewKanbanGroupByMenu';
import { ViewSortingMenu } from '~/components/ViewControls/ViewSortingMenu/ViewSortingMenu';
import { ViewTypeToggleMenu } from '~/components/ViewControls/ViewTypeToggleMenu/ViewTypeToggleMenu';
import { useShowExpandNestedInVisibilityMenu } from '~/components/ViewControls/ViewVisiblityToggleMenu/hooks/useShowExpandNestedInVisibilityMenu';
import { PublicViewVisibilityToggleMenu } from '~/components/ViewControls/ViewVisiblityToggleMenu/PublicViewVisibilityToggleMenu';
import { ViewVisibilityExpandNestedContentToggle } from '~/components/ViewControls/ViewVisiblityToggleMenu/ViewVisibilityExpandNestedContentToggle';
import { ViewControlsBoardHeader } from '~/components/ViewControlsBoardHeader';
import { useClearSearchSortOverrideWhenQueryCleared } from '~/hooks/search/useClearSearchSortOverrideWhenQueryCleared';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { usePublicHeaderHorizontalPadding } from '~/hooks/usePublicHeaderHorizontalPadding';
import { useRerenderOnMount } from '~/hooks/useRerenderOnMount';
import { usePublicBoard } from '~/providers/PublicBoardProvider';
import { centralizedBoardAncestorsSelector, centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { currentKanbanGroupByCustomFieldIdSelector, currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { isViewSortingMenuSupported } from '~/store/configViews/utils';
import { type LocationBoard } from '~/store/router/types';
import { useGetPublicCustomFieldOptions } from '~/swr-hooks/customFields/useGetPublicCustomFieldOptions';
import { canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';

export type PublicBoardViewControlsProps = Pick<ViewControlsProps, 'scrollRef' | 'headerRef'>;

export const PublicBoardViewControls = memo(({ scrollRef, headerRef }: PublicBoardViewControlsProps) => {
  useClearSearchSortOverrideWhenQueryCleared();
  const [isScrolledToTop, setIsScrolledToTop] = useState(false);
  const boardTitle = useSelector(centralizedBoardTitleSelector);
  const ancestors = useSelector(centralizedBoardAncestorsSelector);
  const { isSearchActive } = useIsSearchActive();
  const { onNavigateToBoard } = usePublicBoard();
  const onBoardClick = useCallback(
    (board: LocationBoard) => onNavigateToBoard({ boardId: board.id, boardTitle: board.title }),
    [onNavigateToBoard],
  );
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const inKanbanView = currentViewTypeName === ViewTypeName.kanban;
  const isSortingMenuSupported = isViewSortingMenuSupported(currentViewTypeName);
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { isMounted } = useRerenderOnMount();
  const { permissions } = usePublicPermissions();
  const canViewCustomFields = canSeeBoardCustomFields(permissions);
  const { responsiveHorizontalPadding } = usePublicHeaderHorizontalPadding();
  const { presentationSettings } = useShortIdContext();
  const isExpandNestedDisabled = presentationSettings?.lockedControls?.expandNested === true;
  const { showExpandNestedInVisibilityMenu } = useShowExpandNestedInVisibilityMenu({
    isDisabled: isExpandNestedDisabled,
  });
  const isViewTypeLocked = presentationSettings?.lockedControls?.viewType === true;

  const customFieldId = useSelector(currentKanbanGroupByCustomFieldIdSelector);
  const { data: options } = useGetPublicCustomFieldOptions({ customFieldId });

  const header = useMemo(() => {
    /**
     * We need to check if mounted here because on the server side, isActive is false but on first render,
     * if the user has a search query param in the url, isActive will be true so this check prevents
     * a rehydration error
     *  */
    if (isSearchActive && isMounted) {
      return <PublicSearchResultsNumber />;
    } else if (isScrolledToTop && boardTitle) {
      return <ViewControlsBoardHeader title={boardTitle} ancestors={ancestors} onBoardClick={onBoardClick} />;
    } else if (!isAboveMediumScreen && isSortingMenuSupported) {
      return <ViewSortingMenu />;
    }

    return null;
  }, [
    ancestors,
    boardTitle,
    isAboveMediumScreen,
    isMounted,
    isScrolledToTop,
    isSearchActive,
    isSortingMenuSupported,
    onBoardClick,
  ]);

  const buttons = useMemo(
    () => (
      <>
        {inKanbanView && (
          <div className="mx-1">
            {canViewCustomFields && <ViewKanbanGroupByMenu customFieldValues={options?.data} />}
          </div>
        )}

        {isAboveMediumScreen && isSortingMenuSupported && (
          <div className="mx-1">
            <ViewSortingMenu />
          </div>
        )}

        <div className="mx-1">
          <PublicViewVisibilityToggleMenu
            extraItems={showExpandNestedInVisibilityMenu ? <ViewVisibilityExpandNestedContentToggle /> : undefined}
          />
        </div>
        {!isViewTypeLocked && (
          <div className="ml-1">
            <ViewTypeToggleMenu canViewCustomFields={canViewCustomFields} />
          </div>
        )}
      </>
    ),
    [
      canViewCustomFields,
      inKanbanView,
      isAboveMediumScreen,
      isSortingMenuSupported,
      isViewTypeLocked,
      options?.data,
      showExpandNestedInVisibilityMenu,
    ],
  );

  const style = useMemo(
    () => ({
      paddingLeft: responsiveHorizontalPadding,
      paddingRight: responsiveHorizontalPadding,
    }),
    [responsiveHorizontalPadding],
  );

  return (
    <ViewControls
      headerRef={headerRef}
      scrollRef={scrollRef}
      buttons={buttons}
      header={header}
      onScrolled={setIsScrolledToTop}
      style={style}
      topPosition={PUBLIC_TOP_BAR_HEIGHT}
    />
  );
});

PublicBoardViewControls.displayName = 'PublicBoardViewControls';
